import React, { useEffect, useState } from "react";
import { Search, X, Plus, ShieldAlert, Loader2 } from "lucide-react";
import { api, formatError } from "../lib/api";
import AppShell from "../components/AppShell";

const MAX_ACCOUNTS = 10;

/**
 * Admin cross-account assistant. Requires explicit account IDs; never
 * defaults to "all accounts." Only sees documents with
 * consent.admin_cross_account=true on the specifically selected accounts.
 */
export default function AdminAccountAssistantPage() {
  const [accounts, setAccounts] = useState([]);          // full account list from /admin/accounts
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);          // account_id[]
  const [query, setQuery] = useState("");
  const [reason, setReason] = useState("");
  const [answer, setAnswer] = useState(null);
  const [sources, setSources] = useState([]);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/accounts", { params: { limit: 500 } });
        setAccounts(r.data?.accounts || []);
      } catch (err) {
        setError(formatError(err));
      } finally {
        setLoadingAccounts(false);
      }
    })();
  }, []);

  const filtered = accounts.filter((a) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (a.owner_email || "").toLowerCase().includes(q)
      || (a.owner_name || "").toLowerCase().includes(q)
      || (a.account_id || "").toLowerCase().includes(q);
  }).slice(0, 80);

  const toggle = (accountId) => {
    setSelected((prev) => {
      if (prev.includes(accountId)) return prev.filter((a) => a !== accountId);
      if (prev.length >= MAX_ACCOUNTS) return prev;
      return [...prev, accountId];
    });
  };

  const runQuery = async () => {
    setError(null); setAnswer(null); setSources([]);
    if (selected.length === 0) { setError("Select at least one account."); return; }
    if (reason.trim().length < 8) { setError("A reason of at least 8 characters is required."); return; }
    if (query.trim().length < 3) { setError("Query must have at least 3 characters."); return; }
    setRunning(true);
    try {
      const r = await api.post("/admin/account-assistant/query", {
        query: query.trim(),
        account_ids: selected,
        reason: reason.trim(),
      });
      if (r.data?.error === "provider_error") {
        setError("Provider temporarily unavailable. Try again shortly.");
      }
      setAnswer(r.data?.answer || null);
      setSources(r.data?.sources || []);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <AppShell title="Admin · Account Assistant">
      <div className="w-full py-4 md:py-6" data-testid="admin-assistant-page">
        <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-2">Cross-account query</h1>
        <p className="text-sm text-neutral-600 mb-4 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" />
          Select up to {MAX_ACCOUNTS} accounts explicitly. Retrieval is limited to documents each account has enabled for admin cross-account queries.
        </p>

        <div className="grid gap-6 lg:grid-cols-[minmax(280px,380px)_1fr]">
          {/* Account picker */}
          <div className="rounded-lg border border-neutral-200 bg-white">
            <div className="px-3 py-2 border-b border-neutral-200">
              <div className="flex items-center gap-1 mb-2">
                <Search className="h-4 w-4 text-neutral-400" />
                <input
                  data-testid="admin-assistant-account-search"
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search accounts by owner or id"
                  className="flex-1 text-sm rounded-md border border-neutral-300 px-2 py-1.5"
                />
              </div>
              <div className="text-[11px] text-neutral-500" data-testid="admin-assistant-selected-count">
                Selected: {selected.length} / {MAX_ACCOUNTS}
              </div>
            </div>
            {loadingAccounts ? (
              <div className="p-4 text-sm text-neutral-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading accounts…</div>
            ) : (
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-neutral-100">
                {filtered.map((a) => {
                  const on = selected.includes(a.account_id);
                  const disabled = !on && selected.length >= MAX_ACCOUNTS;
                  return (
                    <li key={a.account_id}>
                      <button
                        data-testid={`admin-assistant-acct-${a.account_id}`}
                        disabled={disabled}
                        onClick={() => toggle(a.account_id)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${on ? "bg-emerald-50" : "hover:bg-neutral-50"} disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <span className={`w-4 h-4 rounded border ${on ? "bg-emerald-600 border-emerald-600" : "border-neutral-300"} grid place-items-center`}>
                          {on && <Plus className="h-3 w-3 text-white rotate-45" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <div className="truncate">{a.owner_name || a.owner_email || a.account_id}</div>
                          <div className="text-[10px] text-neutral-500 truncate">{a.owner_email} · {a.member_count} member{a.member_count === 1 ? "" : "s"}</div>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Query panel */}
          <div className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-neutral-700">Reason (required, auditable)</label>
              <input
                data-testid="admin-assistant-reason"
                value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                placeholder="Why are you running this query?"
                className="mt-1 w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-700">Question</label>
              <textarea
                data-testid="admin-assistant-query"
                value={query} onChange={(e) => setQuery(e.target.value)} rows={4} maxLength={1000}
                placeholder="What do you want to know about the selected accounts?"
                className="mt-1 w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="flex justify-end">
              <button
                data-testid="admin-assistant-run"
                onClick={runQuery} disabled={running}
                className="rounded-md bg-neutral-900 text-white text-sm px-4 py-1.5 disabled:opacity-40 hover:bg-neutral-800"
              >{running ? "Running…" : "Run query"}</button>
            </div>

            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2" data-testid="admin-assistant-error">{error}</div>
            )}

            {answer && (
              <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                <div className="text-xs font-semibold text-neutral-500 uppercase mb-1">Answer</div>
                <div className="text-sm text-neutral-900 whitespace-pre-wrap" data-testid="admin-assistant-answer">{answer}</div>
              </div>
            )}

            {sources.length > 0 && (
              <div>
                <div className="text-xs uppercase font-semibold text-neutral-500 mb-1">Sources</div>
                <ul className="text-xs space-y-1">
                  {sources.map((s, i) => (
                    <li key={i} className="border border-neutral-200 rounded px-2 py-1 bg-white" data-testid={`admin-assistant-source-${i}`}>
                      <div className="flex items-center gap-2 text-neutral-500">
                        <span className="uppercase font-semibold">{s.source}</span>
                        {s.account_id && <code className="font-mono text-[10px]">{s.account_id}</code>}
                        {s.document_id && <code className="font-mono text-[10px]">{s.document_id.slice(0, 8)}</code>}
                      </div>
                      <div className="text-neutral-800 truncate">{s.title}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
